"use client";

import { useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/motion/tabs";
import { emitModuleInteraction } from "@/lib/module-interaction";

export interface SegmentTabOption {
  value: string;
  label: ReactNode;
  count?: number;
  leading?: ReactNode;
}

export interface SegmentTabsProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  options: SegmentTabOption[];
  deselectable?: boolean;
  className?: string;
  listClassName?: string;
}

const NONE = "__none__";

export function SegmentTabs({
  value,
  onValueChange,
  options,
  deselectable = false,
  className,
  listClassName,
}: SegmentTabsProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const handleValueChange = (next: string) => {
    const resolved = deselectable && next === value ? null : next;
    if (resolved !== value) {
      // Option indices instead of values: the values are author-authored
      // labels and must not reach the analytics payload.
      const from = options.findIndex((o) => o.value === value);
      const to = options.findIndex((o) => o.value === resolved);
      emitModuleInteraction(
        rootRef.current,
        "tab_change",
        { kind: "tab", index: to },
        { from, to },
      );
    }
    onValueChange(resolved);
  };

  return (
    <Tabs
      rootRef={rootRef}
      value={value ?? NONE}
      onValueChange={handleValueChange}
      variant="segment"
      className={cn("inline-block", className)}
    >
      <TabsList className={listClassName}>
        {options.map((option) => (
          <TabsTrigger key={option.value} value={option.value}>
            {option.leading && (
              <span className="mr-1 inline-flex shrink-0 items-center">
                {option.leading}
              </span>
            )}
            {option.label}
            {option.count !== undefined && (
              <span className="ml-1 text-[11px] tabular-nums text-[var(--subtle)]">
                {option.count}
              </span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
